import api from '../../config/AxiosService';
import { Batch } from '../../types';
import { Dataset, DatasetFileVersion, DatasetVersionReport, DatasetVersion } from '../../types/dataset';
import { TransformedLabel } from '../../types/label';
import { Pagination } from '../../types';

export const getDatasetsByProject = async (
  projectId: string,
  includeLatestVersion: boolean = false,
  params?: Pagination
): Promise<any> => {
  if (includeLatestVersion) {
    const response = await api.get(
      `/datasets/list/${projectId}?includeLatestVersion=true${params && `&page=${params.page}&size=${params.pageSize}`}`
    );
    return { items: response.data.items, totalItems: response.data.meta.totalItems };
  } else {
    const response = await api.get(`/datasets/list/${projectId}`);
    return response.data;
  }
};

export const getPaginatedDatasets = async (
  projectId: string,
  params: Pagination,
  filter: string
): Promise<{ items: Dataset[]; totalItems: number }> => {
  const response = await api.get(
    `/models/list/${projectId}?page=${params.page}&size=${params.pageSize}` + (filter ? `&name=${filter}` : '')
  );
  return response.data;
};

export const getDataset = async (datasetId: string): Promise<Dataset> => {
  const response = await api.get(`/datasets/${datasetId}`);
  return response.data;
};

export const getDatasetReport = async (datasetId: string): Promise<DatasetVersionReport[]> => {
  const response = await api.get(`/datasets/report/${datasetId}`);
  return response.data;
};

export const changeDatasetSettings = async (settings: {}, datasetVersion: string) => {
  return await api.put(`/dataset-versions/${datasetVersion}/settings`, settings, {
    headers: { 'Content-Type': 'application/json' },
  });
};

export const getLatestDatasetVersion = async (datasetId: string): Promise<DatasetVersion> => {
  const response = await api.get(`/dataset-versions/latest/${datasetId}`);
  return response.data;
};

export const getSecondToLatestDatasetVersion = async (datasetId: string): Promise<DatasetVersion> => {
  const response = await api.get(`/dataset-versions/second-to-last-dataset-version-for-dataset/${datasetId}`);
  return response.data;
};

export const getDatasetVersionById = async (datasetVersionId: string): Promise<DatasetVersion> => {
  const response = await api.get(`/dataset-versions/info/${datasetVersionId}`);
  return response.data;
};

export const commit = async (datasetVersion: string) => {
  const response = await api.post(`/dataset-versions/commit/${datasetVersion}`);
  return response.data;
};

export const getDatasetFiles = async (datasetVersion: string): Promise<DatasetFileVersion[]> => {
  const response = await api.get(`/dataset-files/list/${datasetVersion}`);
  return response.data;
};

export const getPaginatedDatasetFiles = async (
  datasetVersion: string,
  queryString: string,
  params: Pagination
): Promise<any> => {
  const response = await api.get(
    `/dataset-files/datasetVersion/${datasetVersion}/dataset-files?page=${params.page}&size=${params.pageSize}&${queryString}`
  );
  return { items: response.data.items, totalItems: response.data.meta.totalItems };
};

export const getDatasetFileVersions = async (datasetFileId: string): Promise<DatasetFileVersion[]> => {
  const response = await api.get(`/dataset-files/versionlist/${datasetFileId}`);
  return response.data;
};

export const getDatasetFileVersion = async (
  datasetFileVersionId: string,
  addTags: boolean = false
): Promise<DatasetFileVersion> => {
  const response = await api.get(`/dataset-files/version/${datasetFileVersionId}`, {
    params: {
      addTags,
    },
  });
  return response.data;
};

export const getDatasetFileVersionPage = async (
  datasetFileVersionId: string,
  datasetVersionId: string,
  queryString: string
): Promise<number> => {
  const response = await api.get(`/dataset-files/${datasetFileVersionId}/${datasetVersionId}/get-page?${queryString}`);
  return response.data;
};

export const createDataset = async (name: string, projectId: string): Promise<Dataset> => {
  const response = await api.post(`/datasets/new?name=${name}&project=${projectId}`);
  return response.data;
};

export const renameDataset = async (datasetId: string, name: string): Promise<Dataset> => {
  const response = await api.put(`/datasets/${datasetId}`, { name: name });
  return response.data;
};

export const deleteDataset = async (datasetId: string) => {
  await api.delete(`/datasets/${datasetId}`);
};

export const deleteDatasetFileVersion = async (datasetFileVersion: DatasetFileVersion) => {
  await api.delete(`/dataset-files/file/${datasetFileVersion.id}`);
};

export const deleteMultipleDatasetFileVersions = async (records: string[]) => {
  await api.delete(`/dataset-files/bulk`, { data: records });
};

export const scorePreviousDatasetVersion = async (datasetVersionVersionId: string) => {
  await api.post(`/scoring/dataset-version/${datasetVersionVersionId}`);
};

export const getDatasetScoreByAlgorithmVersion = async (
  datasetVersionId: string,
  algorithmVersionId: string
): Promise<any> => {
  const response = await api.get(`/scoring/summary/${algorithmVersionId}/${datasetVersionId}`);
  return response.data;
};

export const setDatasetFileAnnotations = async (
  datasetFileVersionId: string,
  annotations: {
    type?: { reliable: boolean; name: string };
    fields: TransformedLabel[];
    properties?: {};
  }
) => {
  await api.put('/dataset-files/' + datasetFileVersionId + '/annotations', annotations, {
    headers: { 'Content-Type': 'application/json' },
  });
};

export const setDatasetFileProperties = async (datasetFileVersionId: string, properties: {}) => {
  await api.put('/dataset-files/' + datasetFileVersionId + '/properties', properties, {
    headers: { 'Content-Type': 'application/json' },
  });
};

export const addFilesToDataset = async (datasetVersion: string, batchCreationInfo: Batch) => {
  const response = await api.post(`/dataset-files/add/${datasetVersion}`, batchCreationInfo, {
    headers: { 'Content-Type': 'application/json' },
  });
  return response.data;
};

export const addInferenceFilesToDataset = async (datasetVersion: string, batchCreationInfo: Batch) => {
  const response = await api.post(`/dataset-files/add-with-inference/${datasetVersion}`, batchCreationInfo, {
    headers: { 'Content-Type': 'application/json' },
  });
  return response.data;
};
